import { Box, Link, Typography } from "@mui/material";
import type { JSX } from "react";

import type { SalesLeadBrief, SalesOrgLeadDetail } from "@xop/dashboard-shared";

import { SignalKindIcon } from "../../components/SignalKindIcon.js";
import { StrategyMarkdown } from "../../strategy/StrategyMarkdown.js";
import { EvidenceTimeline } from "../EvidenceTimeline.js";
import { Collapsible, CopyButton, ExpandableText, panelInsetSx, Section } from "./primitives.js";

/* ── 3. Outreach ───────────────────────────────────────────────── */

export function OutreachCard({ message }: { message: string }): JSX.Element {
  return (
    <Section
      title="Suggested first message"
      icon="✏"
      action={
        <Box component="span" className="no-print">
          <CopyButton text={message} label="Copy message" />
        </Box>
      }
    >
      <Box sx={[panelInsetSx, { p: 1.5, fontSize: 14 }]}>
        <ExpandableText collapsedHeight={200}>
          <StrategyMarkdown source={message} />
        </ExpandableText>
      </Box>
      <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
        Generated by the agent. Adapt freely — the trigger event is the
        anchor; the rest is editable.
      </Typography>
    </Section>
  );
}

/* ── 4. Bundle / XOP angle ─────────────────────────────────────── */

export function AngleCard({ angle }: { angle: string }): JSX.Element {
  return (
    <Section title="XOP angle" icon="🎯">
      <Box sx={[panelInsetSx, { p: 1.5, fontSize: 14 }]}>
        <ExpandableText collapsedHeight={140}>
          <StrategyMarkdown source={angle} />
        </ExpandableText>
      </Box>
    </Section>
  );
}

/* ── Why now — the full brief, visible but clamped (Show more) so it's never
 * a wall, yet nothing is hidden behind a collapse. ───────────────────────── */

export function WhyNowCard({ brief }: { brief: SalesLeadBrief }): JSX.Element {
  return (
    <Section title={`Why now — full brief v${brief.version}`} icon="📋">
      <Box sx={{ fontSize: 14 }}>
        <ExpandableText collapsedHeight={170}>
          <StrategyMarkdown source={brief.why_now_markdown} />
        </ExpandableText>
      </Box>
    </Section>
  );
}

export function FallbackReasoningCard({ text }: { text: string }): JSX.Element {
  return (
    <Section title="Why the agent surfaced this" icon="📋">
      <ExpandableText collapsedHeight={140}>
        <Typography sx={{ whiteSpace: "pre-line", fontSize: 14, lineHeight: 1.6, color: "text.primary" }}>
          {text}
        </Typography>
      </ExpandableText>
    </Section>
  );
}

/* ── 7. Evidence ───────────────────────────────────────────────── */

export function EvidenceCard({
  signals, signalKinds, pinned,
}: {
  signals: SalesOrgLeadDetail["signals"];
  signalKinds: string[];
  pinned: SalesLeadBrief["evidence"];
}): JSX.Element {
  const totalSources = signals.length + pinned.length;
  return (
    <Collapsible
      title={`Evidence (${totalSources})`}
      icon="🔗"
    >
      {pinned.length > 0 ? (
        <Box sx={{ display: "grid", gap: 0.5, fontSize: 12 }}>
          <Box sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
            Pinned by the brief
          </Box>
          <Box component="ul" sx={{ display: "grid", gap: 0.25, listStyle: "none", m: 0, p: 0 }}>
            {pinned.map((e, i) => (
              <Box component="li" key={i}>
                <Link href={e.url} target="_blank" rel="noreferrer" underline="hover" sx={{ color: "text.primary", textUnderlineOffset: 4 }}>
                  {e.title || e.url}
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
      {signalKinds.length > 0 ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, fontSize: 12, color: "text.secondary" }}>
          {signalKinds.map((k) => (
            <Box component="span" key={k} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, borderRadius: 999, border: 1, borderColor: "divider", px: 1, py: 0.25 }}>
              <SignalKindIcon kind={k} showLabel />
            </Box>
          ))}
        </Box>
      ) : null}
      <EvidenceTimeline signals={signals} />
    </Collapsible>
  );
}
